/**
 * Theme Configuration
 *
 * All colors are derived from a single base hue for easy theme changes.
 * To change the entire color scheme, just update the BASE_HUE value.
 *
 * Colors are provided in both CSS hex strings and PixiJS hex numbers.
 */

// =============================================================================
// BASE HUE - Change this single value to shift the entire color scheme
//
// Common values:
//   142 = Green (radar/military) - current
//   187 = Cyan (original)
//   220 = Blue
//   270 = Purple
//   340 = Pink/Magenta
//   30  = Orange
// =============================================================================
export const BASE_HUE = 142;

export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Backgrounds
  bgPrimary: string;
  bgSurface: string;
  bgElevated: string;

  // Borders
  border: string;
  borderLight: string;

  // Accent colors
  accentPrimary: string;
  accentSecondary: string;

  // Status colors
  success: string;
  error: string;
  warning: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textInverse: string;

  // Special
  headerBg: string;
  modalOverlay: string;
}

export interface PixiThemeColors {
  bgPrimary: number;
  bgSurface: number;
  border: number;
  accentPrimary: number;
  accentSecondary: number;
  success: number;
  error: number;
  warning: number;
  textPrimary: number;
  textSecondary: number;
  textMuted: number;
}

// =============================================================================
// HSL to RGB/Hex conversion utilities
// =============================================================================

/**
 * Convert HSL to RGB hex number (for PixiJS)
 */
function hslToHex(h: number, s: number, l: number): number {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
  else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
  else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
  else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
  else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
  else if (h >= 300 && h < 360) { r = c; g = 0; b = x; }

  const rHex = Math.round((r + m) * 255);
  const gHex = Math.round((g + m) * 255);
  const bHex = Math.round((b + m) * 255);

  return (rHex << 16) | (gHex << 8) | bHex;
}

/**
 * Convert HSL to CSS hex string
 */
function hslToHexString(h: number, s: number, l: number): string {
  const hex = hslToHex(h, s, l);
  return '#' + hex.toString(16).padStart(6, '0');
}

// =============================================================================
// Generated theme colors based on BASE_HUE
// =============================================================================

// Dark theme - softer, less harsh on eyes
export const DARK_THEME: ThemeColors = {
  // Backgrounds - near black with subtle hue tint
  bgPrimary: hslToHexString(BASE_HUE, 15, 4),
  bgSurface: hslToHexString(BASE_HUE, 12, 9),
  bgElevated: hslToHexString(BASE_HUE, 10, 12),

  // Borders - subtle hue tint
  border: hslToHexString(BASE_HUE, 15, 24),
  borderLight: hslToHexString(BASE_HUE, 14, 30),

  // Accent colors
  accentPrimary: hslToHexString(BASE_HUE, 75, 60),
  accentSecondary: hslToHexString(30, 70, 60), // Orange - complementary

  // Status colors
  success: hslToHexString(142, 55, 62),
  error: hslToHexString(0, 65, 65),
  warning: hslToHexString(45, 70, 60),

  // Text colors - slight hue tint for cohesion
  textPrimary: hslToHexString(BASE_HUE, 8, 92),
  textSecondary: hslToHexString(BASE_HUE, 12, 60),
  textMuted: hslToHexString(BASE_HUE, 10, 42),
  textInverse: hslToHexString(BASE_HUE, 20, 8),

  // Special
  headerBg: hslToHexString(BASE_HUE, 15, 8),
  modalOverlay: 'rgba(0, 0, 0, 0.7)',
};

// Light theme - warm, easy on eyes
export const LIGHT_THEME: ThemeColors = {
  // Backgrounds - clean whites with slight hue tint
  bgPrimary: hslToHexString(BASE_HUE, 15, 97),
  bgSurface: hslToHexString(BASE_HUE, 10, 100),
  bgElevated: hslToHexString(BASE_HUE, 10, 100),

  // Borders - soft with hue tint
  border: hslToHexString(BASE_HUE, 12, 85),
  borderLight: hslToHexString(BASE_HUE, 10, 90),

  // Accent colors - deeper for contrast on light bg
  accentPrimary: hslToHexString(BASE_HUE, 70, 40),
  accentSecondary: hslToHexString(30, 65, 48),

  // Status colors - richer for visibility
  success: hslToHexString(142, 50, 38),
  error: hslToHexString(0, 60, 50),
  warning: hslToHexString(45, 80, 38),

  // Text colors
  textPrimary: hslToHexString(BASE_HUE, 25, 15),
  textSecondary: hslToHexString(BASE_HUE, 15, 40),
  textMuted: hslToHexString(BASE_HUE, 10, 58),
  textInverse: hslToHexString(BASE_HUE, 10, 100),

  // Special
  headerBg: hslToHexString(BASE_HUE, 10, 100),
  modalOverlay: 'rgba(0, 0, 0, 0.5)',
};

// PixiJS colors (hex numbers) for dark theme
export const DARK_PIXI: PixiThemeColors = {
  bgPrimary: hslToHex(BASE_HUE, 15, 4),
  bgSurface: hslToHex(BASE_HUE, 12, 9),
  border: hslToHex(BASE_HUE, 15, 24),
  accentPrimary: hslToHex(BASE_HUE, 75, 60),
  accentSecondary: hslToHex(30, 70, 60),
  success: hslToHex(142, 55, 62),
  error: hslToHex(0, 65, 65),
  warning: hslToHex(45, 70, 60),
  textPrimary: hslToHex(BASE_HUE, 8, 92),
  textSecondary: hslToHex(BASE_HUE, 12, 60),
  textMuted: hslToHex(BASE_HUE, 10, 42),
};

// PixiJS colors (hex numbers) for light theme
export const LIGHT_PIXI: PixiThemeColors = {
  bgPrimary: hslToHex(BASE_HUE, 15, 97),
  bgSurface: hslToHex(BASE_HUE, 10, 100),
  border: hslToHex(BASE_HUE, 12, 85),
  accentPrimary: hslToHex(BASE_HUE, 70, 40),
  accentSecondary: hslToHex(30, 65, 48),
  success: hslToHex(142, 50, 38),
  error: hslToHex(0, 60, 50),
  warning: hslToHex(45, 80, 38),
  textPrimary: hslToHex(BASE_HUE, 25, 15),
  textSecondary: hslToHex(BASE_HUE, 15, 40),
  textMuted: hslToHex(BASE_HUE, 10, 58),
};

// Export the accent color for use in visualization.config.ts
export const ACCENT_PRIMARY_HEX = hslToHex(BASE_HUE, 75, 60);

/**
 * Get theme colors by mode
 */
export function getTheme(mode: 'light' | 'dark'): ThemeColors {
  return mode === 'light' ? LIGHT_THEME : DARK_THEME;
}

/**
 * Get PixiJS theme colors by mode
 */
export function getPixiTheme(mode: 'light' | 'dark'): PixiThemeColors {
  return mode === 'light' ? LIGHT_PIXI : DARK_PIXI;
}
